import { cx } from '@/lib/cx';

/*
 * The full SEED EMERGE lockup: SEED top right, EMERGE with the monogram as its
 * E, and the descriptor closing the block. The approved signature.
 *
 * ⚠️ USE THIS WHEREVER IT FITS, AND THE MARK ALONE WHERE IT DOES NOT. It is
 * 2.42:1 and three lines deep, so it needs roughly 190px of width before "SEED"
 * and the descriptor close up — which the sign-in panel, the sign-in card and
 * the approval masthead all have, and the SIDEBAR HEADER does not: 232px of
 * width against 56px of height would need about 96px. That is why the sidebar
 * still renders `EmergeMark` beside real text, and it is not an oversight.
 *
 * ⚠️ IT IS AN IMAGE OF TEXT, WHICH IS A REVERSAL WORTH STATING. Every surface
 * here used to set "SEED EMERGE" as real HTML text beside a decorative mark, so
 * the product name was selectable and searchable. The lockup carries the words
 * inside the artwork instead, so it takes `role="img"` and a real
 * `aria-label` — the accessible name survives, the selectable text does not.
 * That is the cost of using the approved signature rather than rebuilding it in
 * HTML, and the owner chose the signature.
 *
 * ⚠️ INK: `tone="onNavy"` IS NOT DECORATION. The default ink is `fill-fg`, which
 * follows the theme — correct on any app surface, and INVISIBLE on the
 * BrandPanel, whose field is a FIXED seed-navy in the light theme too. A fixed
 * ground needs fixed ink; that is the same trap that killed an earlier cut of
 * the mark, and it does not announce itself in the theme you happen to be in.
 *
 * The E is the same two paths as `EmergeMark`, scaled into a line of 60px type
 * (cap line 49.4, baseline 93). If they ever disagree, `EmergeMark` is the
 * original.
 */

const BODY = 'M14 25H23V33.5H43.5V41H23V45.5H49.5V53H14Z';
const ARM = 'M22.35 13.12L47.95 8.6L49.26 15.99L23.65 20.5Z';

const SANS = 'var(--font-sans)';

interface EmergeLockupProps {
  /** Sizing. Give it a width or a height; the viewBox holds the 2.42:1 ratio. */
  className?: string;
  /**
   * `onNavy` for a FIXED navy or dark ground — the BrandPanel. The default
   * follows the theme and disappears on those grounds in the light theme.
   */
  tone?: 'theme' | 'onNavy';
}

export function EmergeLockup({ className, tone = 'theme' }: EmergeLockupProps) {
  const ink = tone === 'onNavy' ? 'fill-seed-cream' : 'fill-fg';
  return (
    <svg
      viewBox="0 11 271 112"
      className={cx('block h-auto', className)}
      role="img"
      aria-label="SEED EMERGE — Partnerships and Billing"
    >
      <text
        x="200.42"
        y="42"
        className={ink}
        style={{ fontFamily: SANS, fontSize: 18, fontWeight: 600, letterSpacing: '0.3em' }}
      >
        SEED
      </text>

      <g
        className="fill-seed-orange stroke-seed-orange"
        strokeWidth="1.413"
        strokeLinejoin="round"
        transform="translate(-21.80 10.47) scale(1.5571)"
      >
        <path d={BODY} />
        <path d={ARM} />
      </g>

      <text
        x="62.28"
        y="93"
        className={ink}
        style={{ fontFamily: SANS, fontSize: 60, fontWeight: 700, letterSpacing: '-0.005em' }}
      >
        MERGE
      </text>

      <text
        x="0"
        y="116"
        className="fill-seed-orange"
        style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, letterSpacing: '0.22em' }}
      >
        PARTNERSHIPS &amp; BILLING
      </text>
    </svg>
  );
}
